// PLANIMETRIK Calculation Engine
// All calculations use CEIL (Math.ceil) for rounding up

import { ADHESIVES, MATERIALS, UNIT_FACTORS } from "./materialsData";

// Waste percentages by format and pattern
function getWastePercentage(format, pattern) {
  if (format === "Rollo") return 0.12;
  if (format === "Loseta") return 0.10;
  if (format === "Lama") {
    if (pattern === "Espiga") return 0.20;
    if (pattern === "Diagonal") return 0.15;
    return 0.07; // Recta (default)
  }
  return 0.10;
}

// Main calculation function
export function calculateAll(params) {
  const {
    surface_m2,
    material_type,
    format_type,
    installation_type,
    installation_pattern = "Recta",
    piece_width,
    piece_length,
    piece_unit = "cm",
    quantity_per_box,
    soldadura_termica = "No",
    rodapie_enabled = false,
    rodapie_length,
    rodapie_strip_length = "2.4",
    pavimento_esd = false,
    nivelacion = false,
    base_aislante_type = "",
    base_aislante_m2_roll = "",
  } = params;

  const results = {};
  const m2_netos = parseFloat(surface_m2) || 0;

  // 1. Material calculation (m² + waste)
  const wastePercent = getWastePercentage(format_type, installation_pattern);
  const m2_desperdicio = m2_netos * wastePercent;
  const m2_totales = m2_netos + m2_desperdicio;

  results.m2_netos = round2(m2_netos);
  results.m2_desperdicio = round2(m2_desperdicio);
  results.m2_totales = round2(m2_totales);
  results.waste_percent = Math.round(wastePercent * 100);

  // 2. Boxes/Rolls calculation
  if (format_type === "Rollo" && piece_width && piece_length) {
    const kw = UNIT_FACTORS[piece_unit] || 1;
    const rollM2 = (piece_width * kw) * (piece_length * kw);
    if (rollM2 > 0) {
      const rollsNeeded = Math.ceil(m2_totales / rollM2);
      results.rolls = {
        total: rollsNeeded,
        roll_m2: round2(rollM2),
        total_m2_supplied: round2(rollsNeeded * rollM2),
        remaining_m2: round2((rollsNeeded * rollM2) - m2_totales),
      };
    }
  } else if ((format_type === "Lama" || format_type === "Loseta") && quantity_per_box > 0) {
    const boxesNeeded = Math.ceil(m2_totales / quantity_per_box);
    results.boxes = {
      total: boxesNeeded,
      m2_per_box: quantity_per_box,
      total_m2_supplied: round2(boxesNeeded * quantity_per_box),
    };
  }

  // 3. Adhesive calculation
  const adhesiveResult = calculateAdhesive(material_type, format_type, installation_type, m2_totales, pavimento_esd, piece_width, piece_length, piece_unit);
  if (adhesiveResult) {
    results.adhesive = adhesiveResult;
  }

  // 4. Welding cord (Soldadura termica)
  if (soldadura_termica !== "No") {
    const cordResult = calculateWeldingCord(format_type, m2_totales, piece_width, piece_length, piece_unit, soldadura_termica);
    if (cordResult) {
      results.welding_cord = cordResult;
    }
  }

  // 5. Rodapie (skirting board)
  if (rodapie_enabled && rodapie_length > 0) {
    const rodapieResult = calculateRodapie(parseFloat(rodapie_length), rodapie_strip_length);
    results.rodapie = rodapieResult;
  }

  // 6. ESD (copper tape)
  if (pavimento_esd) {
    const esdResult = calculateESD(m2_totales);
    results.esd = esdResult;
  }

  // 7. Leveling (nivelacion)
  if (nivelacion) {
    const levelingResult = calculateLeveling(m2_totales);
    results.leveling = levelingResult;
  }

  // 8. Base aislante (for any Flotante variant + aparte)
  const isFlotante = installation_type && installation_type.toLowerCase().includes("flotante");
  if (isFlotante && base_aislante_type === "aparte" && base_aislante_m2_roll) {
    const m2Roll = parseFloat(base_aislante_m2_roll);
    if (m2Roll > 0) {
      const rollosNeeded = Math.ceil(m2_totales / m2Roll);
      results.base_aislante = {
        rollos: rollosNeeded,
        m2_per_roll: round2(m2Roll),
        total_m2_supplied: round2(rollosNeeded * m2Roll),
      };
    }
  }

  // 8. Budget estimation
  const material = MATERIALS[material_type];
  if (material && material.priceRange) {
    results.budget = {
      min: round2(m2_totales * material.priceRange[0]),
      max: round2(m2_totales * material.priceRange[1]),
      note: "Solo material. Instalacion no incluida.",
    };
  }

  return results;
}

function calculateAdhesive(materialType, formatType, installationType, m2Totales, esdEnabled, pieceW, pieceL, pieceUnit) {
  // Special case: ESD overrides for vinilico pegado
  if (esdEnabled && materialType === "suelo_vinilico" && installationType === "Pegado") {
    const config = ADHESIVES["suelo_vinilico_Pegado_ESD"];
    if (config) {
      const kgTotal = (config.consumption / 1000) * m2Totales;
      return {
        name: config.name,
        type: config.type,
        kg_total: round2(kgTotal),
        containers: Math.ceil(kgTotal / config.containerSize),
        container_size: config.containerSize,
        container_unit: config.containerUnit,
        container_name: config.containerName,
      };
    }
  }

  // Build lookup keys in order of specificity
  const keys = [
    `${materialType}_${formatType}_${installationType}`,
    `${materialType}_${installationType}`,
  ];

  let config = null;
  for (const key of keys) {
    if (ADHESIVES[key]) {
      config = ADHESIVES[key];
      break;
    }
  }

  if (!config) return null;

  if (config.type === "base_aislante") {
    return { name: config.name, type: "base_aislante", m2: round2(m2Totales) };
  }

  if (config.type === "adhesive") {
    const kgTotal = (config.consumption / 1000) * m2Totales;
    return {
      name: config.name,
      type: "adhesive",
      kg_total: round2(kgTotal),
      containers: Math.ceil(kgTotal / config.containerSize),
      container_size: config.containerSize,
      container_unit: config.containerUnit,
      container_name: config.containerName,
    };
  }

  if (config.type === "tape") {
    const mlTotal = config.consumption * m2Totales;
    return {
      name: config.name,
      type: "tape",
      ml_total: round2(mlTotal),
      rolls: Math.ceil(mlTotal / config.containerSize),
      container_size: config.containerSize,
      container_unit: config.containerUnit,
      container_name: config.containerName,
    };
  }

  if (config.type === "tapes") {
    const k = UNIT_FACTORS[pieceUnit] || 0.01;
    const pw = (pieceW || 0) * k;
    const pl = (pieceL || 0) * k;
    const tileArea = pw * pl;
    if (tileArea > 0) {
      const stickersNeeded = Math.ceil(m2Totales / tileArea);
      return {
        name: config.name,
        type: "tapes",
        stickers: stickersNeeded,
        rolls: Math.ceil(stickersNeeded / config.stickersPerRoll),
        stickers_per_roll: config.stickersPerRoll,
      };
    }
    return null;
  }

  if (config.type === "hardware") {
    const items = config.items.map((item) => {
      const quantity = round2(m2Totales * item.factor);
      const result = { name: item.name, quantity, unit: item.unit };
      if (item.containerSize) {
        result.containers = Math.ceil(quantity / item.containerSize);
        result.container_size = item.containerSize;
        result.container_name = item.containerName;
      }
      return result;
    });
    return { type: "hardware", items };
  }

  if (config.type === "cesped_tape" || config.type === "cesped_tape_mesh") {
    // Approximate: joints depend on roll width, simplified as perimeter-based
    const sideLength = Math.sqrt(m2Totales);
    const estimatedJointLength = round2(sideLength * 2);
    const result = {
      name: config.name,
      type: config.type,
      ml_total: estimatedJointLength,
      rolls: Math.ceil(estimatedJointLength / config.containerSize),
      container_size: config.containerSize,
      container_unit: config.containerUnit,
    };
    if (config.type === "cesped_tape_mesh") {
      result.malla_antihierba_m2 = round2(m2Totales);
    }
    return result;
  }

  if (config.type === "none") {
    return { name: config.name, type: "none" };
  }

  return null;
}

function calculateWeldingCord(format, m2, pieceW, pieceL, pieceUnit, cordType) {
  const cordSize = cordType === "Cordon 50 ml" ? 50 : 100;

  if (format === "Rollo") {
    const rolls = Math.ceil((m2 / 2) / cordSize);
    return {
      type: cordType,
      cord_size: cordSize,
      rolls,
    };
  }

  // Loseta or Lama
  if (pieceW && pieceL) {
    const k = UNIT_FACTORS[pieceUnit] || 0.01;
    const xm = pieceW * k;
    const ym = pieceL * k;
    if (xm > 0 && ym > 0) {
      const totalMl = m2 * (1 / xm + 1 / ym);
      const rolls = Math.ceil(totalMl / cordSize);
      return {
        type: cordType,
        cord_size: cordSize,
        total_ml: round2(totalMl),
        rolls,
      };
    }
  }

  return null;
}

function calculateRodapie(lengthM, stripLength) {
  const withWaste = lengthM * 1.07; // +7% waste
  const sl = parseFloat(stripLength) || 2.4;
  const strips = Math.ceil(withWaste / sl);
  return {
    length_m: round2(lengthM),
    with_waste_m: round2(withWaste),
    strips,
    strip_length: sl,
  };
}

function calculateESD(m2Totales) {
  const mlCopper = m2Totales * 0.25;
  const rollsCopper = Math.ceil(mlCopper / 33);
  const mlSupplied = rollsCopper * 33;
  return {
    ml_needed: round2(mlCopper),
    rolls_33ml: rollsCopper,
    ml_supplied: mlSupplied,
  };
}

function calculateLeveling(m2Totales) {
  const autoNivKg = m2Totales * 5.4;
  const bags25kg = Math.ceil(autoNivKg / 25);
  const primerKg = m2Totales * 0.1; // 100 g/m² = 0.1 kg/m²
  const drums5kg = Math.ceil(primerKg / 5);
  return {
    autonivelante_kg: round2(autoNivKg),
    bags_25kg: bags25kg,
    thickness_mm: 3,
    primer_kg: round2(primerKg),
    drums_5kg: drums5kg,
  };
}

function round2(num) {
  return Math.round(num * 100) / 100;
}
